<template>
  <div class="pk-dt">

    <!-- LOADING -->
    <div v-if="loading" class="pk-dt__loading">
      <b-spinner small></b-spinner>
      <span>Loading…</span>
    </div>

    <!-- EMPTY -->
    <div v-else-if="!rows || rows.length === 0" class="pk-dt__empty">
      <slot name="empty">
        <div class="pk-dt__empty-icon">
          <i :class="emptyIcon || 'fas fa-inbox'"></i>
        </div>
        <p class="pk-dt__empty-text">{{ emptyText || 'No items found.' }}</p>
      </slot>
    </div>

    <!-- TABLE -->
    <div v-else class="pk-dt__scroll">
      <table class="pk-dt__table">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="col.thClass"
              :style="col.thStyle"
            >
              <slot :name="'head(' + col.key + ')'">{{ col.label }}</slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in rows"
            :key="rowKey ? row[rowKey] : rowIndex"
            :class="['pk-dt__row', { 'pk-dt__row--clickable': !!$listeners['row-click'] }]"
            @click="onRowClick($event, row)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="col.tdClass"
              :style="col.tdStyle"
            >
              <slot :name="'cell(' + col.key + ')'" :row="row" :value="row[col.key]">
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
export default {
  name: 'PkDataTable',
  props: {
    /**
     * Column definitions.
     * Each item: { key, label, thClass?, thStyle?, tdClass?, tdStyle? }
     */
    columns: {
      type: Array,
      required: true
    },
    /**
     * Row data array.
     */
    rows: {
      type: Array,
      default: function () { return [] }
    },
    /**
     * Show loading state instead of table.
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Property name used as the :key on each row. Falls back to row index.
     */
    rowKey: {
      type: String,
      default: null
    },
    /**
     * Font Awesome icon class for the empty state (e.g. "fas fa-database").
     */
    emptyIcon: {
      type: String,
      default: 'fas fa-inbox'
    },
    /**
     * Text shown in the empty state.
     */
    emptyText: {
      type: String,
      default: 'No items found.'
    }
  },
  methods: {
    onRowClick(event, row) {
      if (event.target.closest('input[type="checkbox"], label[for], .custom-control, .form-group')) {
        return
      }
      this.$emit('row-click', row)
    }
  }
}
</script>

<style scoped>
/* TABLE WRAP */
.pk-dt__scroll {
  overflow-x: auto;
}

/* LOADING */
.pk-dt__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-muted);
  justify-content: center;
}

/* EMPTY */
.pk-dt__empty {
  text-align: center;
  padding: 60px 20px;
}
.pk-dt__empty-icon {
  font-size: 2.5rem;
  color: var(--text-muted);
  margin-bottom: 12px;
  opacity: 0.4;
}
.pk-dt__empty-text {
  color: var(--text-muted);
  margin-bottom: 0;
}

/* TABLE */
.pk-dt__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.pk-dt__table thead tr {
  border-bottom: 1px solid var(--border-default);
}
.pk-dt__table th {
  padding: 12px 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}
.pk-dt__row {
  border-bottom: 1px solid var(--border-default);
  transition: background 0.12s;
}
.pk-dt__row:last-child {
  border-bottom: none;
}
.pk-dt__row:hover {
  background: rgba(255, 255, 255, 0.03);
}
.pk-dt__row--clickable {
  cursor: pointer;
}
.pk-dt__table td {
  padding: 14px 20px;
  color: var(--text-primary);
  vertical-align: middle;
}
</style>
